import CancelButton from 'components/buttons/CancelButton';
import PermissionButton from 'components/buttons/PermissionButton';
import SaveButton from 'components/buttons/SaveButton';
import { useCreateDialogForm, useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { useCreateDraft } from 'components/create-dialog/useCreateDraft';
import { DraftSaveMode } from 'components/drafts/DraftSaveMode';
import ActionHeader from 'components/forms/ActionHeader';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import CustomFields from 'components/forms/inputs/custom-fields/CustomFields';
import Loader from 'components/Loader';
import ConfirmNavigation from 'components/navigation/ConfirmNavigation';
import Section from 'components/section-menu/Section';
import { HighlightedFieldsProvider, SectionActionsProvider } from 'components/section-menu/SectionActionsContext';
import SectionLayout from 'components/section-menu/SectionLayout';
import { useSectionErrors } from 'components/section-menu/useSectionErrors';
import useFocusedEditMode from 'hooks/useFocusedEditMode';
import { useRef } from 'react';
import { Control, FieldValues, FormProvider } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'store';
import { useAddOfficeMutation, useUpdateOfficeMutation } from 'store/api/offices';
import { ClassifierData } from 'types/classifiers';
import { CustomField } from 'types/customFields';
import { OfficeForm } from 'types/offices';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';
import { useGoBack, useLeaveForm, useReturnToLocation } from 'utils/useNavigateWithReturnState';
import { officeFormToRequest } from './officeFormMapping';
import {
    OfficeActiveField,
    OfficeContactFields,
    OfficeFields,
    OfficeInvoiceAbbreviationField,
    OfficeInvoicingFields,
    OfficeLocalAddressField,
} from './form-sections/OfficeFields';
import { useOfficeForm } from './form-sections/useOfficeForm';

type Props = {
    type: 'add' | 'edit';
    initialData?: OfficeForm;
    classifiers: Record<string, ClassifierData>;
    refetch?: () => void;
    entityCustomFields?: CustomField[];
};

const FIELD_TO_SECTION_MAP: Record<string, string> = {
    // General
    company: 'office',
    companies: 'office',
    name: 'office',
    abbreviation: 'office',
    emailAddress: 'office',
    phoneNumber: 'office',
    address: 'office',
    invoiceAbbreviation: 'office',
    localAddress: 'office',
    isActive: 'office',
    // Invoicing and templates
    invoicingEmailAddress: 'offices.invoicing-and-templates',
    deadlineEventEmailAddress: 'offices.invoicing-and-templates',
    invoiceTemplates: 'offices.invoicing-and-templates',
    defaultLocalCountryInvoiceTemplate: 'offices.invoicing-and-templates',
    defaultForeignCountryInvoiceTemplate: 'offices.invoicing-and-templates',
    invoiceSpecTemplates: 'offices.invoicing-and-templates',
    defaultLocalCountryInvoiceSpecTemplate: 'offices.invoicing-and-templates',
    defaultForeignCountryInvoiceSpecTemplate: 'offices.invoicing-and-templates',
};

const OfficePageForm = ({
    type = 'add',
    initialData,
    classifiers,
    refetch,
    entityCustomFields,
}: Props): JSX.Element => {
    useFocusedEditMode('offices');
    const { id: officeId } = useCreateRoute().params;
    const returnTo = useReturnToLocation(officeId ? `/office/${officeId}` : '/offices');
    const goBack = useGoBack(returnTo);
    const leaveForm = useLeaveForm();

    const form = useOfficeForm({ initialData, classifiers, entityCustomFields });
    const { methods, customFields, mapCustomFieldsToDto } = form;
    useCreateDialogForm(methods);
    const draft = useCreateDraft({
        record: 'office',
        methods,
        toPayload: (formData: OfficeForm) =>
            officeFormToRequest(formData, mapCustomFieldsToDto(formData as unknown as Record<string, unknown>)),
    });

    const { handleSubmit, control, reset, formState } = methods;
    const { errors } = formState;

    const sectionErrors = useSectionErrors(errors, FIELD_TO_SECTION_MAP);

    const [addOffice] = useAddOfficeMutation();
    const [updateOffice] = useUpdateOfficeMutation();

    const bypassNavigationBlockerRef = useRef(false);

    const onSubmit = async (formData: OfficeForm, action: 'create' | 'update'): Promise<void> => {
        try {
            const data = officeFormToRequest(
                formData,
                mapCustomFieldsToDto(formData as unknown as Record<string, unknown>)
            );
            if (action === 'create') {
                // A draft is created by completing it: the office keeps the draft's id.
                await (draft.draftId === null ? addOffice(data).unwrap() : draft.complete(data))
                    .then((resp) => {
                        bypassNavigationBlockerRef.current = true;
                        reset({}, { keepValues: true });
                        leaveForm(`/office/${resp.id}`);
                    })
                    .catch(console.error);
            } else if (action === 'update' && officeId && !isNaN(Number(officeId))) {
                await updateOffice({
                    ...data,
                    id: officeId,
                })
                    .unwrap()
                    .then(() => {
                        bypassNavigationBlockerRef.current = true;
                        if (refetch) refetch();
                        reset({}, { keepValues: true });
                        leaveForm(`/office/${officeId}`);
                    })
                    .catch(console.error);
            }
        } catch (e) {
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    const submitCreate = () => {
        handleSubmit((data) => onSubmit(data, 'create'))();
    };

    const submitUpdate = () => {
        handleSubmit((data) => onSubmit(data, 'update'))();
    };

    const { buttonsDisabled } = useSelector((state) => state.buttons);

    if (form.isLoading) {
        return <Loader />;
    }

    // Show inital name if is edit form. Dont do dynamically to prevent unneeded rerenders
    const getFormTitle = () => {
        switch (type) {
            case 'edit':
                return `${initialData?.name ?? ''}${officeInactiveSuffix({ isActive: initialData?.isActive }) ?? ''}`;
            default:
                return t('offices.add-new-office');
        }
    };

    const getFormHeader = () => {
        const actions: JSX.Element[] = [<CancelButton key="cancel" onClick={goBack} />];

        if (type === 'add') {
            actions.push(
                ...(draft.button ? [draft.button] : []),
                <SaveButton
                    key="addSave"
                    permissions={['CREATE.OFFICES']}
                    onClick={submitCreate}
                    disabled={buttonsDisabled}
                />
            );
        } else if (type === 'edit' && officeId) {
            actions.push(
                <PermissionButton
                    key="editSave"
                    permissions={['UPDATE.OFFICES']}
                    color="secondary"
                    variant="contained"
                    size="small"
                    onClick={submitUpdate}
                    disabled={buttonsDisabled}
                >
                    <FormattedMessage id="save" />
                </PermissionButton>
            );
        }

        return <ActionHeader title={getFormTitle()} actions={actions} onBack={goBack} draft={draft.detail} />;
    };

    return (
        <FormProvider {...methods}>
            <SectionActionsProvider>
                <HighlightedFieldsProvider>
                    <FormWrapper>
                        {getFormHeader()}
                        <DraftSaveMode mode={draft.mode}>
                            <SectionLayout sectionErrors={sectionErrors}>
                                <Section title="office">
                                    <OfficeFields form={form} />
                                    <OfficeContactFields form={form} />
                                    <OfficeInvoiceAbbreviationField form={form} />
                                    <OfficeLocalAddressField form={form} />
                                    <OfficeActiveField form={form} />
                                </Section>
                                <Section title={'offices.invoicing-and-templates'}>
                                    <OfficeInvoicingFields form={form} />
                                </Section>
                                {customFields?.length > 0 ? (
                                    <CustomFields
                                        control={control as unknown as Control<FieldValues>}
                                        fields={customFields}
                                    />
                                ) : null}
                            </SectionLayout>
                        </DraftSaveMode>
                    </FormWrapper>
                    <ConfirmNavigation
                        control={control}
                        bypassRef={bypassNavigationBlockerRef}
                        draft={type === 'add' ? draft.leave : null}
                    />
                </HighlightedFieldsProvider>
            </SectionActionsProvider>
        </FormProvider>
    );
};

export default OfficePageForm;
